/**
 * Export History Tab - Exports Page
 *
 * Audit trail of all exports with expandable reconciliation details.
 */

import { useState } from 'react'
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Descriptions,
  Typography,
  Drawer,
  Badge,
  Form,
  DatePicker,
  Select,
} from 'antd'
import { EyeOutlined, DownloadOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { exportService } from '@/services/exportService'
import type { ExportHistoryFilters, ExportHistoryItem, ReconciliationChange } from '../types'

const { Text } = Typography
const { RangePicker } = DatePicker

export function ExportHistoryTab() {
  // State
  const [filters, setFilters] = useState<ExportHistoryFilters>({})
  const [selectedExportId, setSelectedExportId] = useState<string | null>(null)
  const [reconciliationDrawerVisible, setReconciliationDrawerVisible] = useState(false)

  // Fetch export history
  const { data: historyResponse, isLoading } = useQuery({
    queryKey: ['export-history', filters],
    queryFn: () => exportService.getHistory(filters),
    staleTime: 1000 * 60, // 1 minute
  })

  const exportHistory = historyResponse?.data || []

  // Fetch reconciliation report for selected export
  const { data: reconciliationResponse, isLoading: reconciliationLoading } = useQuery({
    queryKey: ['export-reconciliation', selectedExportId],
    queryFn: () => exportService.getReconciliation(selectedExportId!),
    enabled: !!selectedExportId && reconciliationDrawerVisible,
    staleTime: 1000 * 60 * 60, // 1 hour (historical data doesn't change)
  })

  const reconciliation = reconciliationResponse?.data

  const handleViewReconciliation = (exportId: string) => {
    setSelectedExportId(exportId)
    setReconciliationDrawerVisible(true)
  }

  const columns = [
    {
      title: 'Timestamp',
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: (timestamp: string) => dayjs(timestamp).format('MMM DD, YYYY h:mm A'),
      sorter: true,
    },
    {
      title: 'Destination',
      dataIndex: 'destination',
      key: 'destination',
      render: (destination: string) => <Tag>{destination}</Tag>,
    },
    {
      title: 'User',
      dataIndex: 'user',
      key: 'user',
    },
    {
      title: 'Records',
      dataIndex: 'recordCount',
      key: 'recordCount',
      align: 'right' as const,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const color = status === 'success' ? 'green' : status === 'failed' ? 'red' : 'blue'
        return <Tag color={color}>{status.toUpperCase()}</Tag>
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: ExportHistoryItem) => (
        <Space>
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewReconciliation(record.uid)}
          >
            Details
          </Button>
          {record.downloadUrl && (
            <Button
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => window.open(record.downloadUrl)}
            >
              Download
            </Button>
          )}
        </Space>
      ),
    },
  ]

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* Filters */}
      <Card size="small" title="Filters">
        <Form layout="inline">
          <Form.Item label="Date Range">
            <RangePicker
              onChange={(dates) => {
                if (dates && dates[0] && dates[1]) {
                  setFilters({
                    ...filters,
                    dateRange: [dates[0].toISOString(), dates[1].toISOString()],
                  })
                } else {
                  const { dateRange, ...rest } = filters
                  setFilters(rest)
                }
              }}
            />
          </Form.Item>
          <Form.Item label="Destination">
            <Select
              placeholder="All destinations"
              style={{ width: 200 }}
              allowClear
              onChange={(value) => {
                if (value) {
                  setFilters({ ...filters, destination: value })
                } else {
                  const { destination, ...rest } = filters
                  setFilters(rest)
                }
              }}
            >
              <Select.Option value="Square POS">Square POS</Select.Option>
              <Select.Option value="QuickBooks">QuickBooks</Select.Option>
              <Select.Option value="Shopify">Shopify</Select.Option>
              <Select.Option value="Manual CSV">Manual CSV</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item label="Status">
            <Select
              placeholder="All statuses"
              style={{ width: 150 }}
              allowClear
              onChange={(value) => {
                if (value) {
                  setFilters({ ...filters, status: value })
                } else {
                  const { status, ...rest } = filters
                  setFilters(rest)
                }
              }}
            >
              <Select.Option value="success">Success</Select.Option>
              <Select.Option value="failed">Failed</Select.Option>
              <Select.Option value="in_progress">In Progress</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Card>

      {/* Export History Table */}
      <Card>
        <Table
          dataSource={exportHistory}
          columns={columns}
          rowKey="uid"
          loading={isLoading}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `${total} exports`,
          }}
          expandable={{
            expandedRowRender: (record: ExportHistoryItem) => (
              <Descriptions size="small" column={2} bordered>
                <Descriptions.Item label="Total Records">
                  {record.reconciliation?.totalRecords || 0}
                </Descriptions.Item>
                <Descriptions.Item label="New Records">
                  {record.reconciliation?.newRecords || 0}
                </Descriptions.Item>
                <Descriptions.Item label="Updated Records">
                  {record.reconciliation?.updatedRecords || 0}
                </Descriptions.Item>
                <Descriptions.Item label="Unchanged Records">
                  {record.reconciliation?.unchangedRecords || 0}
                </Descriptions.Item>
                {record.reconciliation?.fieldsChanged && (
                  <Descriptions.Item label="Fields Changed" span={2}>
                    {record.reconciliation.fieldsChanged.join(', ')}
                  </Descriptions.Item>
                )}
                {record.errorMessage && (
                  <Descriptions.Item label="Error" span={2}>
                    <Text type="danger">{record.errorMessage}</Text>
                  </Descriptions.Item>
                )}
              </Descriptions>
            ),
          }}
        />
      </Card>

      {/* Reconciliation Drawer */}
      <Drawer
        title="Export Reconciliation"
        open={reconciliationDrawerVisible}
        onClose={() => setReconciliationDrawerVisible(false)}
        width={720}
      >
        {reconciliationLoading ? (
          <Text>Loading reconciliation details...</Text>
        ) : reconciliation ? (
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            {/* Summary */}
            <Card title="Summary" size="small">
              <Descriptions column={2} size="small">
                <Descriptions.Item label="Total Records">
                  {reconciliation.summary.totalRecords}
                </Descriptions.Item>
                <Descriptions.Item label="New Records">
                  <Badge
                    count={reconciliation.summary.newRecords}
                    style={{ backgroundColor: '#52c41a' }}
                  />
                </Descriptions.Item>
                <Descriptions.Item label="Updated Records">
                  <Badge
                    count={reconciliation.summary.updatedRecords}
                    style={{ backgroundColor: '#1890ff' }}
                  />
                </Descriptions.Item>
                <Descriptions.Item label="Unchanged Records">
                  <Badge
                    count={reconciliation.summary.unchangedRecords}
                    style={{ backgroundColor: '#d9d9d9' }}
                  />
                </Descriptions.Item>
              </Descriptions>
            </Card>

            {/* Change Details */}
            <Card title="Change Details" size="small">
              <Table
                dataSource={reconciliation.changes}
                rowKey="productId"
                columns={[
                  { title: 'Product ID', dataIndex: 'productId', key: 'productId' },
                  { title: 'Product Name', dataIndex: 'productName', key: 'productName' },
                  {
                    title: 'Change Type',
                    dataIndex: 'changeType',
                    key: 'changeType',
                    render: (type: string) => {
                      const color =
                        type === 'new'
                          ? 'green'
                          : type === 'updated'
                          ? 'blue'
                          : type === 'deleted'
                          ? 'red'
                          : 'default'
                      return <Tag color={color}>{type.toUpperCase()}</Tag>
                    },
                  },
                ]}
                expandable={{
                  expandedRowRender: (record: ReconciliationChange) => (
                    <Table
                      dataSource={record.fieldChanges}
                      rowKey="field"
                      columns={[
                        { title: 'Field', dataIndex: 'field', key: 'field' },
                        {
                          title: 'Old Value',
                          dataIndex: 'oldValue',
                          key: 'oldValue',
                          render: (v) => <Text delete>{v}</Text>,
                        },
                        {
                          title: 'New Value',
                          dataIndex: 'newValue',
                          key: 'newValue',
                          render: (v) => <Text strong>{v}</Text>,
                        },
                      ]}
                      pagination={false}
                      size="small"
                    />
                  ),
                }}
                pagination={{ pageSize: 10 }}
                size="small"
              />
            </Card>

            {/* Download Actions */}
            <Space>
              <Button icon={<DownloadOutlined />}>Download Changes CSV</Button>
              <Button icon={<DownloadOutlined />}>Download Full Export</Button>
            </Space>
          </Space>
        ) : (
          <Text type="secondary">No reconciliation data available</Text>
        )}
      </Drawer>
    </Space>
  )
}
